import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { Encounter, EncounterType, getDateString, PrenatalExamData } from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'
import { RoundedButton, SolidActionButton } from '../../../components/Buttons/ActionButton'
import { useProfile } from '../../../contexts'
import { useAssessmentExams } from '../../../hooks/backend'
import { ProfileEncounterList } from '../../Encounters/EncountersList/ProfileEncountersList'
import { useLaborCharting } from './context'
import { EducationSummaryPanel } from './EducationSummaryPanel'
import { LabQuickview } from './LabQuickview'
import { PrenatalFlowSheetModal } from './PrenatalFlowSheet'

// ── Preview card shown when prenatal is not the active stage ─

const PrenatalPreview = ({ assessmentId }: { assessmentId: string }) => {
  const { dataArr: examsArr } = useAssessmentExams(assessmentId, EncounterType.ANTEPARTUM)

  const latestExam: (Encounter & { id: string }) | null = useMemo(
    () => (examsArr && examsArr.length > 0 ? (examsArr.find(e => !!e.date) ?? null) : null),
    [examsArr],
  )

  const latestData = latestExam?.data as PrenatalExamData | undefined

  const formatGest = (days?: number) => {
    if (days == null) return null
    return `${Math.floor(days / 7)}w ${days % 7}d`
  }

  const formatBp = (bp?: { systolic: number; diastolic: number }) => {
    if (!bp) return null
    return `${bp.systolic}/${bp.diastolic}`
  }

  const count = examsArr?.length ?? 0

  return (
    <Flex align="center" gap={3} px={4} py={3} flexWrap="wrap">
      <Text
        fontFamily="Hero-New"
        fontWeight={500}
        color="gray.500"
        _dark={{ color: 'gray.400' }}
        fontSize="sm">
        {count} prenatal exam{count !== 1 ? 's' : ''}
      </Text>
      {latestExam && (
        <>
          <Badge colorScheme="gray" fontFamily="Hero-New">
            Last: {latestExam.date ? getDateString(latestExam.date, 'short', true) : '—'}
          </Badge>
          {formatGest(latestData?.gestationalAge) && (
            <Badge colorScheme="blue" fontFamily="Hero-New">
              {formatGest(latestData?.gestationalAge)}
            </Badge>
          )}
          {latestExam.data?.vital?.weight && (
            <Badge colorScheme="green" fontFamily="Hero-New">
              {latestExam.data.vital.weight} lbs
            </Badge>
          )}
          {formatBp(latestExam.data?.vital?.bloodPressure) && (
            <Badge colorScheme="purple" fontFamily="Hero-New">
              BP {formatBp(latestExam.data?.vital?.bloodPressure)}
            </Badge>
          )}
          {latestData?.fetalHeartTones && (
            <Badge colorScheme="pink" fontFamily="Hero-New">
              FHT {latestData.fetalHeartTones} bpm
            </Badge>
          )}
        </>
      )}
    </Flex>
  )
}

// ── Active prenatal section ──────────────────────────────────

const PrenatalActive = () => {
  const { setOpenExam } = useLaborCharting()

  const handleAdmit = useCallback(
    () =>
      setOpenExam({ type: EncounterType.BIRTHING_CENTER_ADMISSION, examId: null, config: null }),
    [setOpenExam],
  )

  return (
    <>
      <Box>
        <ProfileEncounterList type={EncounterType.ANTEPARTUM} onOpenExam={setOpenExam} />
      </Box>
      <Flex px={4} py={2} justify="flex-end" bg="gray.50" _dark={{ bg: 'gray.800' }}>
        <SolidActionButton fontFamily="Hero-New" size="sm" fontWeight={600} onClick={handleAdmit}>
          Admit to Birthing Center
        </SolidActionButton>
      </Flex>
    </>
  )
}

// ── Exported section component ───────────────────────────────

export const PrenatalSection = ({ assessmentId }: { assessmentId: string }) => {
  const { stage, setOpenExam } = useLaborCharting()
  const { userId: patientId } = useProfile()
  const isActive = stage === 'prenatal'
  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isFlowSheetOpen,
    onOpen: openFlowSheet,
    onClose: closeFlowSheet,
  } = useDisclosure()
  const {
    isOpen: isLabQuickviewOpen,
    onOpen: openLabQuickview,
    onClose: closeLabQuickview,
  } = useDisclosure()
  const [flowSheetDefaultView, setFlowSheetDefaultView] = useState<'grid' | 'timeline'>('grid')

  return (
    <Box
      w="100%"
      bg="gray.50"
      _dark={{ bg: 'gray.800' }}
      borderRadius={8}
      boxShadow="0 1px 4px #00000022"
      overflow="hidden">
      {/* Section header — wraps on narrow screens so the quick-view buttons drop below the title */}
      <Flex
        align="center"
        px={4}
        py={2}
        flexWrap="wrap"
        rowGap={1.5}
        borderBottom="1px solid"
        borderColor={isActive ? 'blue.200' : 'gray.100'}
        _dark={{
          borderColor: isActive ? 'blue.600' : 'whiteAlpha.100',
          bg: isActive ? 'blue.900' : 'gray.800',
        }}
        bg={isActive ? 'blue.50' : 'gray.50'}>
        <Text
          fontFamily="Hero-New"
          fontWeight={700}
          fontSize="md"
          color={isActive ? 'blue.700' : 'gray.500'}
          _dark={{ color: isActive ? 'blue.200' : 'gray.400' }}>
          PRENATAL
        </Text>
        {isActive && (
          <Badge
            ml={3}
            borderRadius="full"
            px={2}
            colorScheme="blue"
            fontFamily="Hero-New"
            fontSize="xs">
            Active
          </Badge>
        )}
        <Flex ml="auto" gap={2} flexWrap="wrap" rowGap={1.5} justify="flex-end">
          <RoundedButton
            size="xs"
            colorScheme="blue"
            fontFamily="Hero-New"
            onClick={openLabQuickview}>
            Lab Results
          </RoundedButton>
          <RoundedButton
            size="xs"
            colorScheme="purple"
            fontFamily="Hero-New"
            onClick={() => {
              setFlowSheetDefaultView('timeline')
              openFlowSheet()
            }}>
            Episode Timeline
          </RoundedButton>
          <RoundedButton
            size="xs"
            colorScheme="teal"
            fontFamily="Hero-New"
            onClick={() => {
              setFlowSheetDefaultView('grid')
              openFlowSheet()
            }}>
            Flow Sheet
          </RoundedButton>
          {!isActive && (
            <Button
              size="sm"
              boxShadow="md"
              colorScheme="blue"
              fontFamily="Hero-New"
              onClick={onOpen}>
              View
            </Button>
          )}
        </Flex>
      </Flex>
      <Box bg="white" _dark={{ bg: 'gray.900' }}>
        {isActive ? <PrenatalActive /> : <PrenatalPreview assessmentId={assessmentId} />}
      </Box>
      <EducationSummaryPanel assessmentId={assessmentId} />
      {/* Encounter list modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius={8}>
          <ModalHeader
            fontFamily="Hero-New"
            bg="gray.50"
            borderTopRadius={8}
            borderBottom="1px solid #cdcdcd"
            py={2}
            px={4}>
            Prenatal Care
          </ModalHeader>
          <ModalBody bg="gray.100" p={0}>
            <ProfileEncounterList type={EncounterType.ANTEPARTUM} onOpenExam={setOpenExam} />
          </ModalBody>
          <ModalCloseButton />
        </ModalContent>
      </Modal>
      {/* Flow Sheet modal */}
      <PrenatalFlowSheetModal
        isOpen={isFlowSheetOpen}
        onClose={closeFlowSheet}
        assessmentId={assessmentId}
        onOpenLabQuickview={openLabQuickview}
        defaultView={flowSheetDefaultView}
      />

      {/* Lab Results Quickview */}
      {patientId && (
        <LabQuickview
          assessmentId={assessmentId}
          patientId={patientId}
          isOpen={isLabQuickviewOpen}
          onClose={closeLabQuickview}
        />
      )}
    </Box>
  )
}
